import { getCurrentLocale, uiText } from '../../utils/i18n';
import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './TasksPanel.module.css';

interface ScheduledTask {
  id: string;
  type: 'one-time';
  botName: string;
  chatId: string;
  prompt: string;
  executeAt: string;
  sendCards: boolean;
  label?: string;
  status: string;
  createdAt: string;
}

interface RecurringTask {
  id: string;
  type: 'recurring';
  botName: string;
  chatId: string;
  prompt: string;
  cronExpr: string;
  timezone: string;
  nextExecuteAt: string;
  lastExecutedAt?: string;
  sendCards: boolean;
  label?: string;
  status: string;
  createdAt: string;
}

type AnyTask = (ScheduledTask & { _kind: 'one-time' }) | (RecurringTask & { _kind: 'recurring' });

interface Webhook {
  id: string;
  name: string;
  botName: string;
  chatId: string;
  promptTemplate: string;
  status: string;
  triggerCount: number;
  lastTriggeredAt: string | null;
  createdAt: string;
  secret?: string;
  url: string;
}

type InnerTab = 'scheduled' | 'webhook';

interface TasksPanelProps {
  botName: string;
  chatId: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const locale = getCurrentLocale();
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `${relative.format(0, 'day')} ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `${relative.format(1, 'day')} ${time}`;
  return d.toLocaleDateString(locale, { month: 'long', day: 'numeric' }) + ' ' + time;
}

function formatFullTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(getCurrentLocale(), {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = d.getTime() - now;
  if (diff < 0) return uiText("已过期");
  const relative = new Intl.RelativeTimeFormat(getCurrentLocale(), { numeric: 'always' });
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return relative.format(mins, 'minute');
  const hours = Math.floor(mins / 60);
  if (hours < 24) return relative.format(hours, 'hour');
  const days = Math.floor(hours / 24);
  return relative.format(days, 'day');
}

function cronToText(expr: string): string {
  const aliases: Record<string, string> = {
    '@daily': '每天', '@hourly': '每小时', '@weekly': '每周', '@monthly': '每月',
  };
  if (aliases[expr]) return uiText(aliases[expr]);

  const parts = expr.split(/\s+/);
  if (parts.length !== 5) return uiText("自定义周期");
  const [min, hour, dom, mon, dow] = parts;

  if (min.startsWith('*/') && hour === '*' && dom === '*' && mon === '*' && dow === '*')
    return `${uiText('每')} ${min.slice(2)} ${uiText('分钟')}`;
  if (min !== '*' && hour.startsWith('*/') && dom === '*' && mon === '*' && dow === '*')
    return `${uiText('每')} ${hour.slice(2)} ${uiText('小时')}`;

  const timeStr = hour !== '*' && min !== '*' ? `${hour.padStart(2, '0')}:${min.padStart(2, '0')}` : '';
  const dowNames: Record<string, string> = {
    '0': '日', '7': '日', '1': '一', '2': '二', '3': '三', '4': '四', '5': '五', '6': '六',
  };

  if (dom === '*' && mon === '*' && dow === '*' && timeStr) return `${uiText('每天')} ${timeStr}`;
  if (dom === '*' && mon === '*' && dow !== '*' && timeStr) {
    if (dow === '1-5') return `${uiText('工作日')} ${timeStr}`;
    if (dow === '0,6' || dow === '6,0') return `${uiText('周末')} ${timeStr}`;
    const days = dow.split(',').map((d) => uiText(dowNames[d] || d)).join(uiText('、'));
    return `${uiText('每周')}${days} ${timeStr}`;
  }
  if (dom !== '*' && mon === '*' && dow === '*' && timeStr) return `${uiText('每月')} ${dom} ${uiText('日')} ${timeStr}`;

  return uiText("自定义周期");
}

/* ================================================================
   Task Detail Modal
   ================================================================ */

function TaskDetailModal({
  task,
  onClose,
  onSave,
  onPause,
  onResume,
  onDelete,
}: {
  task: AnyTask;
  onClose: () => void;
  onSave: (id: string, updates: { label?: string; prompt?: string }) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [editLabel, setEditLabel] = useState(task.label || '');
  const [editPrompt, setEditPrompt] = useState(task.prompt);
  const [saving, setSaving] = useState(false);

  const isRecurring = task._kind === 'recurring';
  const rt = isRecurring ? (task as RecurringTask & { _kind: 'recurring' }) : null;
  const ot = !isRecurring ? (task as ScheduledTask & { _kind: 'one-time' }) : null;

  const hasChanges = editLabel !== (task.label || '') || editPrompt !== task.prompt;

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    await onSave(task.id, {
      label: editLabel.trim() || undefined,
      prompt: editPrompt.trim(),
    });
    setSaving(false);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <div className={`${styles.modalAccent} ${isRecurring ? styles.accentRecurring : styles.accentOneTime}`} />
            <span className={styles.modalType}>{isRecurring ? uiText("重复任务") : uiText("单次任务")}</span>
          </div>
          <button className={styles.modalClose} onClick={onClose}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Task name */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{uiText("任务名称")}</label>
            <input
              className={styles.fieldInput}
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              placeholder={uiText("给任务取个名字...")}
            />
          </div>

          {/* Prompt */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{uiText("执行指令")}</label>
            <textarea
              className={styles.fieldTextarea}
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              rows={3}
            />
          </div>

          {/* Info rows */}
          <div className={styles.infoGrid}>
            {isRecurring && rt && (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{uiText("执行频率")}</span>
                  <span className={styles.infoValue}>{cronToText(rt.cronExpr)}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{uiText("下次执行")}</span>
                  <span className={styles.infoValue}>
                    {formatTime(rt.nextExecuteAt)}
                    <span className={styles.infoSub}>（{relativeTime(rt.nextExecuteAt)}）</span>
                  </span>
                </div>
                {rt.lastExecutedAt && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>{uiText("上次执行")}</span>
                    <span className={styles.infoValue}>{formatFullTime(rt.lastExecutedAt)}</span>
                  </div>
                )}
              </>
            )}
            {!isRecurring && ot && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{uiText("执行时间")}</span>
                <span className={styles.infoValue}>
                  {formatTime(ot.executeAt)}
                  <span className={styles.infoSub}>（{relativeTime(ot.executeAt)}）</span>
                </span>
              </div>
            )}
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{uiText("状态")}</span>
              <span className={styles.infoValue}>
                <span className={`${styles.statusDot} ${task.status === 'active' || task.status === 'pending' ? styles.dotActive : styles.dotPaused}`} />
                {task.status === 'active' ? uiText("运行中") : task.status === 'paused' ? uiText("已暂停") : task.status === 'pending' ? uiText("等待中") : task.status}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{uiText("创建时间")}</span>
              <span className={styles.infoValue}>{formatFullTime(task.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className={styles.modalFooter}>
          <div className={styles.footerLeft}>
            <button className={styles.dangerBtn} onClick={() => { onDelete(task.id); onClose(); }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              {uiText("删除")}
            </button>
            {isRecurring && (
              task.status === 'active' ? (
                <button className={styles.secondaryBtn} onClick={() => { onPause(task.id); onClose(); }}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                  {uiText("暂停")}
                </button>
              ) : (
                <button className={styles.secondaryBtn} onClick={() => { onResume(task.id); onClose(); }}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6,4 20,12 6,20" />
                  </svg>
                  {uiText("恢复")}
                </button>
              )
            )}
          </div>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!hasChanges || saving || !editPrompt.trim()}
          >
            {saving ? uiText("保存中...") : uiText("保存修改")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   TasksPanel
   ================================================================ */

export function TasksPanel({ botName, chatId }: TasksPanelProps) {
  const [innerTab, setInnerTab] = useState<InnerTab>('scheduled');
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [recurringTasks, setRecurringTasks] = useState<RecurringTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<AnyTask | null>(null);

  const token = localStorage.getItem('novax:token') || '';

  const fetchTasks = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch('/api/schedule', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTasks(data.tasks || []);
      setRecurringTasks(data.recurringTasks || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleSave = useCallback(async (id: string, updates: { label?: string; prompt?: string }) => {
    try {
      const res = await fetch(`/api/schedule/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSelectedTask(null);
      fetchTasks();
    } catch (err) {
      setError((err as Error).message);
    }
  }, [token, fetchTasks]);

  const handlePause = useCallback(async (id: string) => {
    try {
      await fetch(`/api/schedule/${id}/pause`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchTasks();
    } catch (err) { setError((err as Error).message); }
  }, [token, fetchTasks]);

  const handleResume = useCallback(async (id: string) => {
    try {
      await fetch(`/api/schedule/${id}/resume`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchTasks();
    } catch (err) { setError((err as Error).message); }
  }, [token, fetchTasks]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await fetch(`/api/schedule/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchTasks();
    } catch (err) { setError((err as Error).message); }
  }, [token, fetchTasks]);

  const botTasks = tasks.filter((t) => t.botName === botName);
  const botRecurring = recurringTasks.filter((t) => t.botName === botName);
  const totalCount = botTasks.length + botRecurring.length;

  return (
    <div className={styles.container}>
      {/* Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onSave={handleSave}
          onPause={handlePause}
          onResume={handleResume}
          onDelete={handleDelete}
        />
      )}

      {/* Inner tabs */}
      <div className={styles.innerTabs}>
        <button
          className={`${styles.innerTab} ${innerTab === 'scheduled' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('scheduled')}
        >
          {uiText("定时任务")}{totalCount > 0 && ` (${totalCount})`}
        </button>
        <button
          className={`${styles.innerTab} ${innerTab === 'webhook' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('webhook')}
        >
          Webhook
        </button>
      </div>

      {innerTab === 'scheduled' ? (
        <div className={styles.content}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          {loading ? (
            <div className={styles.loadingWrap}>Loading...</div>
          ) : totalCount === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className={styles.emptyTitle}>{uiText("暂无定时任务")}</div>
              <div className={styles.emptyText}>
                {uiText("在对话中让")} {botName} {uiText("设定定时任务，例如\"每天早上9点给我发日报\"")}
              </div>
            </div>
          ) : (
            <div className={styles.taskList}>
              {botRecurring.map((task) => (
                <div
                  key={task.id}
                  className={styles.taskCard}
                  onClick={() => setSelectedTask({ ...task, _kind: 'recurring' })}
                >
                  <div className={`${styles.cardAccent} ${styles.accentRecurring}`} />
                  <div className={styles.cardBody}>
                    <div className={styles.cardTop}>
                      <div className={styles.cardTitleRow}>
                        <span className={styles.cardTitle}>{task.label || task.prompt.slice(0, 50)}</span>
                        <span className={`${styles.statusDot} ${task.status === 'active' ? styles.dotActive : styles.dotPaused}`} />
                      </div>
                      {task.label && <div className={styles.cardPrompt}>{task.prompt}</div>}
                    </div>
                    <div className={styles.cardBottom}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaFreq}>
                          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
                            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                          </svg>
                          {cronToText(task.cronExpr)}
                        </span>
                        <span className={styles.metaNext}>
                          {uiText("下次：")}{formatTime(task.nextExecuteAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {botTasks.map((task) => (
                <div
                  key={task.id}
                  className={styles.taskCard}
                  onClick={() => setSelectedTask({ ...task, _kind: 'one-time' })}
                >
                  <div className={`${styles.cardAccent} ${styles.accentOneTime}`} />
                  <div className={styles.cardBody}>
                    <div className={styles.cardTop}>
                      <div className={styles.cardTitleRow}>
                        <span className={styles.cardTitle}>{task.label || task.prompt.slice(0, 50)}</span>
                        <span className={`${styles.statusDot} ${styles.dotActive}`} />
                      </div>
                      {task.label && <div className={styles.cardPrompt}>{task.prompt}</div>}
                    </div>
                    <div className={styles.cardBottom}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaFreq}>
                          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {uiText("单次执行")}
                        </span>
                        <span className={styles.metaNext}>
                          {uiText("执行时间：")}{formatTime(task.executeAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <WebhookTab botName={botName} chatId={chatId} />
      )}
    </div>
  );
}

/* ================================================================
   Webhook Tab
   ================================================================ */

function WebhookTab({ botName, chatId }: { botName: string; chatId: string }) {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTemplate, setNewTemplate] = useState('收到 Webhook 事件:\n{{payload}}');
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const token = localStorage.getItem('novax:token') || '';

  const fetchWebhooks = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch('/api/webhooks', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setWebhooks(data.webhooks || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/webhooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newName.trim(), botName, chatId, promptTemplate: newTemplate }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      // Show the secret once
      setSelectedWebhook(created);
      setNewName('');
      setNewTemplate(uiText("收到 Webhook 事件:\n{{payload}}"));
      fetchWebhooks();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (wh: Webhook) => {
    const newStatus = wh.status === 'active' ? 'paused' : 'active';
    try {
      await fetch(`/api/webhooks/${wh.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchWebhooks();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/webhooks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const botWebhooks = webhooks.filter((w) => w.botName === botName);

  const fullUrl = (wh: Webhook) => {
    const base = window.location.origin;
    return `${base}/hook/${wh.id}?secret=${wh.secret || '***'}`;
  };

  return (
    <div className={styles.content}>
      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* Webhook detail modal */}
      {selectedWebhook && (
        <div className={styles.modalOverlay} onClick={() => setSelectedWebhook(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <div className={`${styles.modalAccent}`} style={{ background: '#7c5cbf' }} />
                <span className={styles.modalType}>Webhook</span>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedWebhook(null)}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{uiText("名称")}</label>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{selectedWebhook.name}</div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{uiText("Webhook URL（含 Secret）")}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{
                    flex: 1, fontSize: 11, padding: '8px 10px', background: 'rgba(27,27,20,0.04)',
                    border: '1px solid rgba(27,27,20,0.12)', borderRadius: 6, wordBreak: 'break-all', lineHeight: 1.5,
                  }}>
                    {fullUrl(selectedWebhook)}
                  </code>
                  <button
                    className={styles.secondaryBtn}
                    onClick={() => copyToClipboard(fullUrl(selectedWebhook), selectedWebhook.id)}
                    style={{ flexShrink: 0 }}
                  >
                    {copiedId === selectedWebhook.id ? uiText("已复制") : uiText("复制")}
                  </button>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{uiText("Prompt 模板")}</label>
                <code style={{
                  fontSize: 12, padding: '8px 10px', background: 'rgba(27,27,20,0.04)',
                  border: '1px solid rgba(27,27,20,0.12)', borderRadius: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                }}>
                  {selectedWebhook.promptTemplate}
                </code>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{uiText("用法示例")}</label>
                <code style={{
                  fontSize: 11, padding: '8px 10px', background: 'rgba(27,27,20,0.04)',
                  border: '1px solid rgba(27,27,20,0.12)', borderRadius: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                }}>
                  {`curl -X POST "${fullUrl(selectedWebhook)}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"event": "push", "repo": "my-app"}'`}
                </code>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{uiText("状态")}</span>
                  <span className={styles.infoValue}>
                    <span className={`${styles.statusDot} ${selectedWebhook.status === 'active' ? styles.dotActive : styles.dotPaused}`} />
                    {selectedWebhook.status === 'active' ? uiText("运行中") : uiText("已暂停")}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{uiText("触发次数")}</span>
                  <span className={styles.infoValue}>{selectedWebhook.triggerCount}</span>
                </div>
                {selectedWebhook.lastTriggeredAt && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>{uiText("上次触发")}</span>
                    <span className={styles.infoValue}>{formatFullTime(selectedWebhook.lastTriggeredAt)}</span>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{uiText("创建时间")}</span>
                  <span className={styles.infoValue}>{formatFullTime(selectedWebhook.createdAt)}</span>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <div className={styles.footerLeft}>
                <button className={styles.dangerBtn} onClick={() => handleDelete(selectedWebhook.id)}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                  {uiText("删除")}
                </button>
                <button className={styles.secondaryBtn} onClick={() => { handleToggle(selectedWebhook); setSelectedWebhook(null); }}>
                  {selectedWebhook.status === 'active' ? uiText("暂停") : uiText("恢复")}
                </button>
              </div>
              <button className={styles.saveBtn} onClick={() => setSelectedWebhook(null)}>
                {uiText("关闭")}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingWrap}>Loading...</div>
      ) : (
        <>
          {/* Create form */}
          <div style={{
            padding: '14px 16px', marginBottom: 16,
            background: 'var(--bg-paper-lt)', border: '1px solid rgba(27,27,20,0.15)',
            borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1b1b14' }}>
              {uiText("创建 Webhook")}
            </div>
            <input
              className={styles.fieldInput}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={uiText("Webhook 名称，如 GitHub Push...")}
              style={{ fontSize: 13 }}
            />
            <textarea
              className={styles.fieldTextarea}
              value={newTemplate}
              onChange={(e) => setNewTemplate(e.target.value)}
              rows={2}
              style={{ fontSize: 12 }}
              placeholder={uiText("Prompt 模板，用 {{payload}} 表示收到的数据")}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'rgba(27,27,20,0.45)' }}>
                {uiText("外部系统 POST 到此 URL 即可触发")} {botName}
              </span>
              <button
                className={styles.saveBtn}
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                style={{ padding: '6px 16px', fontSize: 12 }}
              >
                {creating ? uiText("创建中...") : uiText("创建")}
              </button>
            </div>
          </div>

          {/* Webhook list */}
          {botWebhooks.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <div className={styles.emptyTitle}>{uiText("暂无 Webhook")}</div>
              <div className={styles.emptyText}>
                {uiText("创建 Webhook 后，外部系统（GitHub、Stripe、Sentry 等）可以通过 HTTP POST 自动触发")} {botName} {uiText("处理事件")}
              </div>
            </div>
          ) : (
            <div className={styles.taskList}>
              {botWebhooks.map((wh) => (
                <div
                  key={wh.id}
                  className={styles.taskCard}
                  onClick={() => setSelectedWebhook(wh)}
                >
                  <div className={styles.cardAccent} style={{ background: '#7c5cbf' }} />
                  <div className={styles.cardBody}>
                    <div className={styles.cardTop}>
                      <div className={styles.cardTitleRow}>
                        <span className={styles.cardTitle}>{wh.name}</span>
                        <span className={`${styles.statusDot} ${wh.status === 'active' ? styles.dotActive : styles.dotPaused}`} />
                      </div>
                      <div className={styles.cardPrompt}>/hook/{wh.id.slice(0, 8)}...</div>
                    </div>
                    <div className={styles.cardBottom}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaFreq}>
                          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                          </svg>
                          {uiText("已触发")} {wh.triggerCount} {uiText("次")}
                        </span>
                        {wh.lastTriggeredAt && (
                          <span className={styles.metaNext}>
                            {uiText("上次：")}{formatTime(wh.lastTriggeredAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
